import {
  addDays,
  centsFromDecimal,
  formatCivilDate,
  formatCurrency,
  formatPercent,
  isCivilDate,
} from '@eis/core';
import type { InstanceApi } from '@eis/insuranceservice-sdk';

/**
 * How a premium READS (SDD-021 §9): amounts, tax rates, periods, reasons, the engine stamp.
 *
 * Presentation only. Every amount on screen is a decimal string the SERVICE computed, formatted
 * through `formatCurrency` (rule 6); nothing here adds, splits or rounds a premium (prompt 29
 * Decision 9). A string that is not a decimal is shown as it came — printing 0,00 € for a figure
 * that failed to parse would be the one answer worse than showing nothing.
 */

/** The placeholder for a figure the service did not state. */
export const NOT_STATED = '—';

/** A decimal string from the wire, in the reader's locale: `"8.59"` → `8,59 €`. */
export function formatAmount(
  decimal: string | undefined,
  currency: string | undefined,
  locale: string,
): string {
  if (decimal === undefined || decimal.trim() === '') return NOT_STATED;
  const cents = centsFromDecimal(decimal);
  const code = currency === undefined || currency === '' ? 'EUR' : currency;
  if (cents === null) return `${decimal} ${code}`;
  return formatCurrency(cents, code, locale);
}

/** A difference, signed: `+0,80 €`, `−0,80 €`, `0,00 €`. */
export function formatDelta(
  decimal: string | undefined,
  currency: string | undefined,
  locale: string,
): string {
  const text = formatAmount(decimal, currency, locale);
  if (text === NOT_STATED) return text;
  const cents = centsFromDecimal(decimal ?? '');
  return cents !== null && cents > 0 ? `+${text}` : text;
}

/**
 * A tax rate as the reader writes it: `"0.19"` → `19 %` (de) / `19%` (en). The rate is a decimal
 * string on the wire; `Intl` decides the spacing and the sign. Up to two fraction digits
 * (`0.075` → `7,5 %`); anything that is not a number is shown as it came.
 */
export function formatRate(rate: string | undefined, locale: string): string {
  if (rate === undefined || rate.trim() === '') return '';
  if (!/^-?\d+(\.\d+)?$/.test(rate.trim())) return rate;
  return new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 2 }).format(
    Number(rate),
  );
}

/**
 * The value an aggregation adjustment applied (`appliedValue`, or a tier's `value`): a percentage
 * for PERCENT and TIERED_PERCENT (`"-2"` → `−2 %`), money per month for AMOUNT and MINIMUM.
 */
export function formatAdjustmentValue(
  type: string | undefined,
  value: string | undefined,
  currency: string | undefined,
  locale: string,
): string {
  if (value === undefined || value === '') return NOT_STATED;
  if (type === 'PERCENT' || type === 'TIERED_PERCENT') {
    const number = Number(value);
    return Number.isFinite(number) ? formatPercent(number, locale, 0) : value;
  }
  return formatAmount(value, currency, locale);
}

/** The premium periods the console names (`insurance:premium.period.*`). */
export const PREMIUM_PERIODS = ['PER_MONTH', 'PER_YEAR', 'PER_DAY', 'ONE_TIME'] as const;
export type PremiumPeriod = (typeof PREMIUM_PERIODS)[number];

/** The period a premium is owed for; PER_MONTH when the snapshot predates `period` (SDD-015 B-6). */
export function periodOf(period: string | undefined): PremiumPeriod {
  return (PREMIUM_PERIODS as readonly string[]).includes(period ?? '')
    ? (period as PremiumPeriod)
    : 'PER_MONTH';
}

/** The estimate reasons the console names; anything else is shown by its code. */
export const ESTIMATE_REASONS = [
  'NONE',
  'VERSION_CHANGED',
  'GROUP_FACTORS_CHANGED',
  'ENGINE_CHANGED',
] as const;
export type EstimateReason = (typeof ESTIMATE_REASONS)[number];
export const isKnownReason = (reason: string): reason is EstimateReason =>
  (ESTIMATE_REASONS as readonly string[]).includes(reason);

/** The engine failures an estimate names (`estimate.error.code`). */
export const ENGINE_ERRORS = [
  'PREMIUM_ENGINE_UNAVAILABLE',
  'PREMIUM_CALCULATION_FAILED',
  'FACTOR_KEY_UNKNOWN',
] as const;
export const isKnownEngineError = (code: string): code is (typeof ENGINE_ERRORS)[number] =>
  (ENGINE_ERRORS as readonly string[]).includes(code);

/** The tax modes (SDD-021 PC-12). */
export const TAX_MODES = ['DERIVED', 'INCLUDED', 'AUTHORED'] as const;
export type TaxMode = (typeof TAX_MODES)[number];
export const taxModeOfPremium = (premium: InstanceApi.Premium | undefined): TaxMode | undefined =>
  (TAX_MODES as readonly string[]).includes(premium?.taxMode ?? '')
    ? (premium?.taxMode as TaxMode)
    : undefined;

/** One tax code, as a row of a comparison: the code and the rate it is charged at. */
export interface TaxCodeRow {
  readonly code: string;
  readonly rate: string;
}

/**
 * The tax codes of several figures, in the order they first appear — so an actual and an estimated
 * premium line up per code even when one of them has a code the other does not.
 */
export function taxCodesOf(
  ...figures: readonly (readonly InstanceApi.TaxAmount[] | undefined)[]
): TaxCodeRow[] {
  const rows: TaxCodeRow[] = [];
  for (const taxes of figures) {
    for (const tax of taxes ?? []) {
      const code = tax.code ?? '';
      if (rows.some((row) => row.code === code)) continue;
      rows.push({ code, rate: tax.rate ?? '' });
    }
  }
  return rows;
}

/** One code's amount in a figure, if the figure states it. */
export const taxAmountOf = (
  taxes: readonly InstanceApi.TaxAmount[] | undefined,
  code: string,
): string | undefined => taxes?.find((tax) => (tax.code ?? '') === code)?.amount;

/** One code's net in a figure (the net that code's tax is computed on), if stated. */
export const taxNetOf = (
  taxes: readonly InstanceApi.TaxAmount[] | undefined,
  code: string,
): string | undefined => taxes?.find((tax) => (tax.code ?? '') === code)?.net;

/**
 * The single "Steuer" figure of a total: the service's `taxTotal`, or — with exactly one code —
 * that code's amount (the same number). With several codes and no `taxTotal` there is no single
 * figure to show, and none is made up by adding.
 */
export function taxTotalOf(totals: InstanceApi.PremiumTotals | undefined): string | undefined {
  if (totals?.taxTotal !== undefined && totals.taxTotal !== '') return totals.taxTotal;
  const taxes = totals?.tax ?? [];
  return taxes.length === 1 ? taxes[0]?.amount : undefined;
}

/** The per-period figures of a premium as a PremiumTotals — the same strings, regrouped. */
export function totalsOfPremium(
  premium: InstanceApi.Premium | undefined,
): InstanceApi.PremiumTotals {
  return {
    ...(premium?.net?.amount === undefined ? {} : { net: premium.net.amount }),
    ...(premium?.tax === undefined ? {} : { tax: premium.tax }),
    ...((premium?.gross ?? premium?.amount)?.amount === undefined
      ? {}
      : { gross: (premium?.gross ?? premium?.amount)?.amount }),
  };
}

/** The currency of a premium, from whichever figure carries one. */
export const currencyOfPremium = (premium: InstanceApi.Premium | undefined): string =>
  (premium?.gross ?? premium?.amount ?? premium?.monthly ?? premium?.net)?.currency ?? 'EUR';

/** The engine stamp's parts, empty ones dropped (`pcs`, `<version>`, `eis-pcs/1`). */
export function engineStampOf(engine: InstanceApi.PremiumEngine | undefined): string {
  return [engine?.name, engine?.version].filter((part) => (part ?? '') !== '').join(' ');
}

/** A half-open period's two ends as a reader says them: first day and LAST day. */
export function periodLabelOf(
  from: string,
  to: string,
  locale: string,
): { readonly from: string; readonly to: string } {
  return {
    from: isCivilDate(from) ? formatCivilDate(from, locale) : from,
    to: isCivilDate(to) ? formatCivilDate(addDays(to, -1), locale) : to,
  };
}

/** A pro-rata fraction as the service writes it (`"15/30"`), or nothing for a whole month. */
export const proRataOf = (proRata: string | undefined): string | undefined =>
  proRata === undefined || proRata === '' ? undefined : proRata;
